/**
 * @fileoverview Ilan Arama Hook'u
 *
 * Bu modul, Emlakjet ilanlarini aramak icin kullanilan temel hook'u icerir.
 * ChatGPT'nin `window.openai.callTool` API'si ile `emlakjet.search_listings`
 * aracini cagirarak ilan arama islemlerini gerceklestirir.
 *
 * Hook, sayfalama (pagination), arama gecmisi yonetimi ve bolgesel fiyat
 * bilgisi getirme islevlerini de icerir.
 *
 * @module useListingSearch
 * @see {@link https://www.emlakjet.com} Emlakjet
 */

import { useState, useCallback } from "react";
import { useSearchAPI } from "./useSearchAPI";
import { useSearchHistory } from "./useSearchHistory";
import { notifySearchResults } from "./useSearchAlerts";

/**
 * @typedef {import('../types/listing.js').Listing} Listing
 * @typedef {import('../types/listing.js').SearchOutput} SearchOutput
 */

/**
 * @typedef {Object} UseListingSearchParams
 * @property {Object} widgetState - Current widget state
 * @property {Function} updateWidgetState - Function to update widget state
 * @property {Function} [setRegionPriceInfo] - Function to set region price info
 */

/**
 * @typedef {Object} UseListingSearchReturn
 * @property {SearchOutput|null} output - Search output data
 * @property {Function} setOutput - Set output directly
 * @property {boolean} isCalling - Whether a search is in progress
 * @property {string|null} callError - Error message if search failed
 * @property {Function} setCallError - Set error message
 * @property {boolean} isLoadingMore - Whether loading more results
 * @property {boolean} regionLoading - Whether loading region price
 * @property {Function} callSearch - Execute a search with path and filters
 * @property {Function} loadMore - Load more results (pagination)
 * @property {Function} loadRegionPrice - Load region price info
 */

/**
 * Ilan arama islemleri icin ana custom hook.
 *
 * Bu hook, Emlakjet API'si ile ilan arama, sayfalama ve arama durumu
 * yonetimi islemlerini saglar. MCP `emlakjet.search_listings` aracini
 * cagirarak aramalari gerceklestirir.
 *
 * **Temel Islevler:**
 * - `callSearch`: Yeni arama baslatir
 * - `loadMore`: Sonraki sayfa sonuclarini yukler (pagination)
 * - `loadRegionPrice`: Bolgesel fiyat bilgisi getirir
 *
 * **State Yonetimi:**
 * - Arama gecmisi widget state'inde tutulur (son 10 arama)
 * - Tekrarlayan aramalar gecmisten filtrelenir
 * - Her aramada secili ilan ID'si sifirlanir
 *
 * **ChatGPT Entegrasyonu:**
 * - Her arama sonucunda ChatGPT'ye bildirim gonderilir
 * - Arama istatistikleri (fiyat ortalamasi, min/max) ChatGPT ile paylasilir
 * - Akilli uyarilar (cok filtre, tekrarli arama vb.) eklenir
 *
 * @function useListingSearch
 * @param {UseListingSearchParams} params - Hook parametreleri
 * @param {Object} params.widgetState - Mevcut widget state'i
 * @param {Function} params.updateWidgetState - Widget state guncelleme fonksiyonu
 * @param {Function} [params.setRegionPriceInfo] - Bolgesel fiyat bilgisi set fonksiyonu
 * @returns {UseListingSearchReturn} Hook donusumu
 *
 * @example
 * const {
 *   output,
 *   isCalling,
 *   callError,
 *   callSearch,
 *   loadMore,
 *   loadRegionPrice,
 * } = useListingSearch({
 *   widgetState,
 *   updateWidgetState,
 *   setRegionPriceInfo,
 * });
 *
 * // Yeni arama
 * callSearch({ path: "satilik-konut/istanbul", filters: "oda-sayisi=3-1" });
 *
 * // Sonraki sayfa
 * loadMore(output.query, output.page, output.listings);
 */
export function useListingSearch({
  widgetState,
  updateWidgetState,
  setRegionPriceInfo,
}) {
  // Output state (bu hook'ta kaliyor cunku orchestrator'un sorumluluğu)
  const [output, setOutput] = useState(null);

  // API islemleri icin alt hook
  const {
    isCalling,
    callError,
    setCallError,
    isLoadingMore,
    regionLoading,
    executeSearch,
    executeLoadMore,
    executeRegionPrice,
  } = useSearchAPI();

  // Arama gecmisi yonetimi icin alt hook
  const { searchHistory, addToHistory } = useSearchHistory(widgetState, updateWidgetState);

  /**
   * Arama calistir ve state'leri guncelle.
   */
  const callSearch = useCallback(async ({ path, filters, sort }) => {
    const result = await executeSearch({ path, filters, sort });

    if (result) {
      const { structured, normalizedPath, normalizedFilters } = result;

      setOutput(structured);
      setRegionPriceInfo?.(null);

      // ChatGPT'ye bildirim gonder
      notifySearchResults(structured, normalizedPath, normalizedFilters, searchHistory);

      // Gecmise ekle
      const entry = {
        path: typeof structured?.query?.path === "string"
          ? structured.query.path
          : normalizedPath,
        filters: typeof structured?.query?.filters === "string"
          ? structured.query.filters
          : normalizedFilters ?? null,
      };
      addToHistory(entry);

      // Secili ilan'i sifirla
      updateWidgetState((state) => ({
        ...state,
        privateContent: {
          ...state.privateContent,
          selectedListingId: null,
        },
      }));
    }
  }, [executeSearch, searchHistory, addToHistory, updateWidgetState, setRegionPriceInfo]);

  /**
   * Daha fazla sonuc yukle ve mevcut listeye ekle.
   */
  const loadMore = useCallback(async (query, page, sort) => {
    const structured = await executeLoadMore(query, page, sort);

    if (structured) {
      setOutput((prev) => ({
        ...structured,
        listings: [...(prev?.listings || []), ...structured.listings],
      }));
    }
  }, [executeLoadMore]);

  /**
   * Bolgesel fiyat bilgisi getir.
   */
  const loadRegionPrice = useCallback(async (path, filters) => {
    const structured = await executeRegionPrice(path, filters);

    if (structured) {
      setRegionPriceInfo?.(structured);
    }
  }, [executeRegionPrice, setRegionPriceInfo]);

  return {
    output,
    setOutput,
    isCalling,
    callError,
    setCallError,
    isLoadingMore,
    regionLoading,
    callSearch,
    loadMore,
    loadRegionPrice,
  };
}

export default useListingSearch;
